import type { RunState } from './runState';
import type { PartnerType } from './shopCatalog';
import { getTunables } from '../config/tunables';
import {
  getRelicDefinition,
  isMagicPartner,
  isShootingPartner,
  isSupportPartner,
  RELIC_CATALOG,
  RelicId,
} from './relicCatalog';

export interface RelicContext {
  wave: number;
  materials: number;
  hp: number;
  maxHp: number;
  partnerTypes: readonly PartnerType[];
}

export interface RelicModifiers {
  maxHpDelta: number;
  moveSpeedBonus: number;
  pickupRangeBonus: number;
  flatDamageBonus: number;
  damageBonus: number;
  attackSpeedBonus: number;
  projectileSpeedBonus: number;
  materialGainBonus: number;
  xpGainBonus: number;
  damageTakenBonus: number;
  critChanceBonus: number;
  critMultiplier: number;
  martialRangeBonus: number;
  martialDamageBonus: number;
  shootingDamageBonus: number;
  shootingFireRateBonus: number;
  shootingProjectileSpeedBonus: number;
  shootingPierceBonus: number;
  magicRangeBonus: number;
  magicDamageBonus: number;
  supportEffectBonus: number;
  supportCooldownBonus: number;
  supportDamageTakenReduction: number;
  tankBlockFrequencyBonus: number;
  thornDamage: number;
  shieldCooldownMs: number;
  shieldDurationMs: number;
  killHeal: number;
  lightningChance: number;
  lightningDamage: number;
  waveEndHeal: number;
  sellRefundRate: number;
  shopDiscount: number;
  hasFreeReroll: boolean;
  shopCandidateLimit: number;
  enemyHpBonus: number;
}

export interface OwnedRelicStack {
  id: RelicId;
  count: number;
  definition: ReturnType<typeof getRelicDefinition>;
}

export interface RelicContributionSummary {
  id: RelicId;
  name: string;
  stacks: number;
  damageBonus: number;
  attackSpeedBonus: number;
  maxHpDelta: number;
  materialGainBonus: number;
}

export const EMPTY_RELIC_MODIFIERS: RelicModifiers = {
  maxHpDelta: 0,
  moveSpeedBonus: 0,
  pickupRangeBonus: 0,
  flatDamageBonus: 0,
  damageBonus: 0,
  attackSpeedBonus: 0,
  projectileSpeedBonus: 0,
  materialGainBonus: 0,
  xpGainBonus: 0,
  damageTakenBonus: 0,
  critChanceBonus: 0,
  critMultiplier: 2,
  martialRangeBonus: 0,
  martialDamageBonus: 0,
  shootingDamageBonus: 0,
  shootingFireRateBonus: 0,
  shootingProjectileSpeedBonus: 0,
  shootingPierceBonus: 0,
  magicRangeBonus: 0,
  magicDamageBonus: 0,
  supportEffectBonus: 0,
  supportCooldownBonus: 0,
  supportDamageTakenReduction: 0,
  tankBlockFrequencyBonus: 0,
  thornDamage: 0,
  shieldCooldownMs: 0,
  shieldDurationMs: 0,
  killHeal: 0,
  lightningChance: 0,
  lightningDamage: 0,
  waveEndHeal: 0,
  sellRefundRate: 0.5,
  shopDiscount: 0,
  hasFreeReroll: false,
  shopCandidateLimit: 4,
  enemyHpBonus: 0,
};

export class RelicSystem {
  private readonly ownedStacks = new Map<RelicId, number>();
  private lastShieldAt = -Infinity;
  private bloodlustStacks = 0;
  private bloodlustExpiresAt = 0;
  private lastWaveCompleteApplied = 0;

  getOwnedRelics() {
    return this.getOwnedStacks().flatMap((stack) => Array.from({ length: stack.count }, () => stack.definition));
  }

  getOwnedIds() {
    return this.getOwnedStacks().flatMap((stack) => Array.from({ length: stack.count }, () => stack.id));
  }

  getOwnedCount() {
    return this.getOwnedStacks().reduce((total, stack) => total + stack.count, 0);
  }

  canBuy(id: RelicId) {
    const relic = getRelicDefinition(id);
    const count = this.ownedStacks.get(id) ?? 0;
    return relic.maxStacks === undefined || count < relic.maxStacks;
  }

  buyRelic(id: RelicId) {
    const relic = getRelicDefinition(id);
    const current = this.ownedStacks.get(id) ?? 0;
    if (relic.maxStacks !== undefined && current >= relic.maxStacks) {
      throw new Error(`Relic stack is capped: ${id} x${relic.maxStacks}`);
    }
    this.ownedStacks.set(id, current + 1);
  }

  getAvailableRelics() {
    return RELIC_CATALOG.filter((relic) => this.canBuy(relic.id));
  }

  getOwnedStacks(): OwnedRelicStack[] {
    return Array.from(this.ownedStacks.entries()).map(([id, count]) => ({
      id,
      count,
      definition: getRelicDefinition(id),
    }));
  }

  getContributionSummary(context: RelicContext): RelicContributionSummary[] {
    return Array.from(this.ownedStacks.entries()).map(([id, count]) => {
      const definition = getRelicDefinition(id);
      const modifiers = { ...EMPTY_RELIC_MODIFIERS };
      applyRelic(id, count, modifiers, context);
      return {
        id,
        name: definition.name,
        stacks: count,
        damageBonus:
          modifiers.damageBonus +
          modifiers.martialDamageBonus +
          modifiers.shootingDamageBonus +
          modifiers.magicDamageBonus,
        attackSpeedBonus: modifiers.attackSpeedBonus + modifiers.shootingFireRateBonus + modifiers.supportCooldownBonus,
        maxHpDelta: modifiers.maxHpDelta,
        materialGainBonus: modifiers.materialGainBonus,
      };
    });
  }

  getModifiers(context: RelicContext): RelicModifiers {
    const modifiers = { ...EMPTY_RELIC_MODIFIERS };
    modifiers.sellRefundRate = getTunables().pricing.sellRefundRate;

    for (const [id, count] of this.ownedStacks.entries()) {
      applyRelic(id, count, modifiers, context);
    }

    if (this.bloodlustStacks > 0) {
      modifiers.damageBonus += 0.25 * this.bloodlustStacks;
    }

    modifiers.lightningChance = Math.min(1, modifiers.lightningChance);
    modifiers.damageTakenBonus = Math.max(-0.85, modifiers.damageTakenBonus);
    modifiers.shopCandidateLimit = Math.max(3, Math.min(4, modifiers.shopCandidateLimit));
    modifiers.sellRefundRate = Math.max(0, Math.min(1, modifiers.sellRefundRate));
    modifiers.shopDiscount = Math.max(0, Math.min(0.8, modifiers.shopDiscount));
    return modifiers;
  }

  applyWaveComplete(runState: RunState, wave: number, context: RelicContext) {
    if (this.lastWaveCompleteApplied === wave) {
      return;
    }
    this.lastWaveCompleteApplied = wave;
    const modifiers = this.getModifiers(context);
    if (modifiers.waveEndHeal > 0) {
      runState.recover(modifiers.waveEndHeal);
    }
  }

  tryShield(timeMs: number, context: RelicContext) {
    const modifiers = this.getModifiers(context);
    if (modifiers.shieldCooldownMs <= 0) {
      return false;
    }
    if (timeMs - this.lastShieldAt < modifiers.shieldCooldownMs) {
      return false;
    }
    this.lastShieldAt = timeMs;
    return true;
  }

  recordKill(timeMs: number) {
    if (!this.ownedStacks.has('bloodlust')) {
      return;
    }
    const bloodlustRelicStacks = this.ownedStacks.get('bloodlust') ?? 1;
    this.bloodlustStacks = Math.min(3 * bloodlustRelicStacks, this.bloodlustStacks + bloodlustRelicStacks);
    this.bloodlustExpiresAt = timeMs + 2000;
  }

  update(timeMs: number) {
    if (this.bloodlustStacks > 0 && timeMs >= this.bloodlustExpiresAt) {
      this.bloodlustStacks = 0;
    }
  }
}

function applyRelic(id: RelicId, stackCount: number, modifiers: RelicModifiers, context: RelicContext) {
  const shootingCount = context.partnerTypes.filter(isShootingPartner).length;
  const supportCount = context.partnerTypes.filter(isSupportPartner).length;
  const emptySlots = Math.max(0, 6 - context.partnerTypes.length);
  const hpRatio = context.maxHp > 0 ? context.hp / context.maxHp : 1;
  const rawStacks = Math.max(1, stackCount);
  const stacks = getEffectiveStacks(id, rawStacks);

  if (id === 'iron-skin') modifiers.maxHpDelta += 20 * stacks;
  if (id === 'swift-boots') modifiers.moveSpeedBonus += 0.15 * stacks;
  if (id === 'magnet') modifiers.pickupRangeBonus += 0.4 * stacks;
  if (id === 'whetstone') modifiers.damageBonus += 0.12 * stacks;
  if (id === 'ammo-pouch') modifiers.attackSpeedBonus += 0.12 * stacks;
  if (id === 'thorn-armor') modifiers.thornDamage += 6 * stacks;
  if (id === 'life-tree') modifiers.waveEndHeal += 10 * stacks;
  if (id === 'shield-shard') {
    modifiers.shieldCooldownMs = Math.min(modifiers.shieldCooldownMs || 3000, Math.max(1000, 3000 - (stacks - 1) * 650));
    modifiers.shieldDurationMs = 500 + (stacks - 1) * 150;
  }
  if (id === 'battle-spirit') modifiers.killHeal += stacks;
  if (id === 'lightning-chain') {
    modifiers.lightningChance += 0.3 * stacks;
    modifiers.lightningDamage += 4 * stacks;
  }
  if (id === 'swordmaster-mark') modifiers.martialRangeBonus += 0.3 * stacks;
  if (id === 'sharpshooter') {
    modifiers.shootingProjectileSpeedBonus += 0.25 * stacks;
    modifiers.shootingPierceBonus += stacks;
  }
  if (id === 'elemental-heart') modifiers.magicRangeBonus += 0.25 * stacks;
  if (id === 'healer-sigil') {
    modifiers.supportEffectBonus += 0.4 * stacks;
    modifiers.tankBlockFrequencyBonus += 0.15 * stacks;
  }
  if (id === 'berserker-axe' && hpRatio < 0.4) modifiers.martialDamageBonus += 0.35 * stacks;
  if (id === 'barrage') modifiers.shootingFireRateBonus += shootingCount * 0.08 * stacks;
  if (id === 'resonance-crystal' && context.partnerTypes.some(isMagicPartner)) modifiers.magicDamageBonus += 0.15 * stacks;
  if (id === 'support-protocol') modifiers.supportDamageTakenReduction += supportCount * 0.1 * stacks;
  if (id === 'blood-oath') {
    modifiers.damageBonus += 0.3 * stacks;
    modifiers.maxHpDelta -= 25 * stacks;
  }
  if (id === 'greed') {
    modifiers.materialGainBonus += 0.5 * stacks;
    modifiers.damageTakenBonus += 0.25 * stacks;
  }
  if (id === 'glass-cannon') {
    modifiers.critChanceBonus += 0.2 * stacks;
    modifiers.critMultiplier = Math.max(modifiers.critMultiplier, 2.5 + (stacks - 1) * 0.35);
    modifiers.maxHpDelta -= 20 * stacks;
  }
  if (id === 'lone-wolf') modifiers.damageBonus += emptySlots * 0.12 * stacks;
  if (id === 'time-bomb') {
    modifiers.attackSpeedBonus += 0.1 * stacks;
    modifiers.enemyHpBonus += 0.15 * stacks;
  }
  if (id === 'gambler-dice') modifiers.damageBonus += 0.15 * stacks;
  if (id === 'forgotten-contract') {
    modifiers.damageBonus += 0.2 * stacks;
    modifiers.attackSpeedBonus += 0.15 * stacks;
    modifiers.shopCandidateLimit = 3;
  }
  if (id === 'merchant-pass') modifiers.sellRefundRate = Math.max(modifiers.sellRefundRate, 0.8);
  if (id === 'gold-rush') modifiers.materialGainBonus += (context.wave <= 3 ? 0.5 : -0.3) * stacks;
  if (id === 'free-reroll') modifiers.hasFreeReroll = true;
  if (id === 'investment-return') modifiers.damageBonus += Math.min(0.2 * stacks, Math.floor(context.materials / 10) * 0.02 * stacks);
  if (id === 'small-blade') modifiers.flatDamageBonus += 2 * stacks;
  if (id === 'field-ration') modifiers.waveEndHeal += 6 * stacks;
  if (id === 'pickup-bell') modifiers.pickupRangeBonus += 0.2 * stacks;
  if (id === 'bronze-scope') modifiers.projectileSpeedBonus += 0.15 * stacks;
  if (id === 'lucky-chip') modifiers.materialGainBonus += 0.12 * stacks;
  if (id === 'quick-spring') modifiers.attackSpeedBonus += 0.08 * stacks;
  if (id === 'rusty-nail') modifiers.flatDamageBonus += stacks;
  if (id === 'cloth-boots') modifiers.moveSpeedBonus += 0.06 * stacks;
  if (id === 'tiny-magnet') modifiers.pickupRangeBonus += 0.1 * stacks;
  if (id === 'copper-coin') modifiers.materialGainBonus += 0.06 * stacks;
  if (id === 'training-weight') modifiers.maxHpDelta += 8 * stacks;
  if (id === 'wooden-shield') modifiers.damageTakenBonus -= 0.05 * stacks;
  if (id === 'herb-tea') modifiers.waveEndHeal += 4 * stacks;
  if (id === 'spark-plug') modifiers.attackSpeedBonus += 0.05 * stacks;
  if (id === 'chalk-mark') modifiers.critChanceBonus += 0.04 * stacks;
  if (id === 'warm-glove') modifiers.martialRangeBonus += 0.08 * stacks;
  if (id === 'paper-fan') modifiers.magicRangeBonus += 0.08 * stacks;
  if (id === 'focus-thread') modifiers.shootingProjectileSpeedBonus += 0.08 * stacks;
  if (id === 'sharp-blade') modifiers.flatDamageBonus += 3 * stacks;
  if (id === 'precision-ring') modifiers.critChanceBonus += 0.08 * stacks;
  if (id === 'power-core') modifiers.damageBonus += 0.18 * stacks;
  if (id === 'guardian-buckle') modifiers.damageTakenBonus -= 0.12 * stacks;
  if (id === 'collector-bag') modifiers.materialGainBonus += 0.22 * stacks;
  if (id === 'long-barrel') {
    modifiers.shootingDamageBonus += 0.18 * stacks;
    modifiers.shootingProjectileSpeedBonus += 0.15 * stacks;
  }
  if (id === 'arcane-ink') {
    modifiers.magicDamageBonus += 0.18 * stacks;
    modifiers.magicRangeBonus += 0.1 * stacks;
  }
  if (id === 'piercing-needle') modifiers.shootingPierceBonus += 2 * stacks;
  if (id === 'overload-coil') {
    modifiers.attackSpeedBonus += 0.25 * stacks;
    modifiers.damageTakenBonus += 0.1 * stacks;
  }
  if (id === 'vampire-fang') {
    modifiers.killHeal += 2 * stacks;
    modifiers.maxHpDelta -= 10 * stacks;
  }
  if (id === 'gold-magnet') {
    modifiers.pickupRangeBonus += 0.75 * stacks;
    modifiers.materialGainBonus += 0.15 * stacks;
  }
  if (id === 'soul-link') {
    modifiers.killHeal += 2 * stacks;
    modifiers.supportEffectBonus += 0.25 * stacks;
    modifiers.damageTakenBonus -= 0.08 * stacks;
  }
  if (id === 'echo-chamber') {
    modifiers.lightningChance += 0.4 * stacks;
    modifiers.lightningDamage += 6 * stacks;
  }
  if (id === 'armor-piercer') {
    modifiers.shootingPierceBonus += 2 * stacks;
    modifiers.damageBonus += 0.1 * stacks;
  }
  if (id === 'endless-vault') modifiers.shopDiscount += 0.1 * stacks;
  if (id === 'world-resonance') {
    modifiers.damageBonus += 0.3 * stacks;
    modifiers.attackSpeedBonus += 0.2 * stacks;
    modifiers.maxHpDelta += 30 * stacks;
  }
  if (id === 'relic-printer') {
    modifiers.materialGainBonus += 0.35 * stacks;
    modifiers.damageBonus += 0.15 * stacks;
  }
}

function getEffectiveStacks(id: RelicId, stackCount: number) {
  const relic = getRelicDefinition(id);
  const diminishingFactor = relic.diminishingFactor ?? 1;
  if (diminishingFactor === 1 || stackCount <= 1) {
    return stackCount;
  }
  if (!Number.isFinite(diminishingFactor) || diminishingFactor < 0 || diminishingFactor > 1) {
    throw new Error(`Invalid diminishing factor for relic ${id}: ${diminishingFactor}`);
  }
  return 1 + (stackCount - 1) * diminishingFactor;
}
